import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { JobPostingTagRequest, RecruitmentUserOption, VacancyListItem, VacancyOption } from './vacancyTypes';

/** The Vacancies list adds the unit and job-posting facets to the shared contract. No status axis. */
interface VacancyQuery extends Omit<ListQuery, 'status'> {
  /** Comma-separated org-unit ids. */
  unit?: string;
  /** Comma-separated: `tagged`, `untagged`. */
  jobPosting?: string;
}

function toQueryString(query: VacancyQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  // Sent whenever present, empty string included — an axis ticked to nothing matches no row.
  if (query.unit !== undefined) params.set('unit', query.unit);
  if (query.jobPosting !== undefined) params.set('jobPosting', query.jobPosting);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const vacanciesApi = {
  list: (query: VacancyQuery, signal?: AbortSignal) =>
    api<PagedResult<VacancyListItem>>(`/api/vacancies${toQueryString(query)}`, { signal }),

  get: (itemId: string, signal?: AbortSignal) => api<VacancyListItem>(`/api/vacancies/${itemId}`, { signal }),

  /** Vacant items not yet tagged — the tag form's picker. Unpaged, like every option feed. */
  options: (signal?: AbortSignal) => api<VacancyOption[]>('/api/vacancies/options', { signal }),

  /**
   * One item per call: the route names the item, so each tag lands in that item's History Logs.
   * The Job Posting stage rides on it — who posted it, when, the remarks.
   */
  tag: (itemId: string, request: JobPostingTagRequest) =>
    api<VacancyListItem>(`/api/vacancies/${itemId}/job-posting`, { method: 'POST', body: request }),

  /** Corrects the LIVE posting's Job Posting stage. */
  updatePosting: (itemId: string, request: JobPostingTagRequest) =>
    api<VacancyListItem>(`/api/vacancies/${itemId}/job-posting`, { method: 'PUT', body: request }),

  /** Live accounts for "Posted by" and "Screened by" — opened by either module's write keys. */
  userOptions: (signal?: AbortSignal) => api<RecruitmentUserOption[]>('/api/vacancies/user-options', { signal }),

  /** Soft — the tag row stays, inactive. */
  untag: (itemId: string) => api<void>(`/api/vacancies/${itemId}/job-posting`, { method: 'DELETE' }),

  /**
   * Publishes the live posting — it takes applicants and Deliberate lists it from now on. One item per
   * call, so each lands in its own item's History Logs. One-way.
   */
  publish: (itemId: string) => api<VacancyListItem>(`/api/vacancies/${itemId}/job-posting/publish`, { method: 'POST' }),
};
